import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate } from '@/utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { 
  Camera, 
  CheckCircle, 
  XCircle, 
  Loader2,
  RefreshCw,
  UserCircle,
  Trash2
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FaceEnrollment = ({ open, onOpenChange, onEnrollmentComplete }) => {
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [loadingModels, setLoadingModels] = useState(true);
  const [cameraActive, setCameraActive] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const [faceDetected, setFaceDetected] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [capturedDescriptor, setCapturedDescriptor] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const [enrollmentStatus, setEnrollmentStatus] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const detectionIntervalRef = useRef(null);

  // Load face-api models
  useEffect(() => {
    const loadModels = async () => {
      setLoadingModels(true);
      
      // Get the base URL for models - try local first, then CDN fallback
      const localModelUrl = window.location.origin + '/models';
      const cdnModelUrl = 'https://cdn.jsdelivr.net/npm/@vladmandic/face-api@1.7.12/model';
      
      // Retry logic with CDN fallback
      const maxRetries = 2;
      let lastError = null;
      let modelUrl = localModelUrl;
      
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.log(`Loading face-api models (attempt ${attempt}/${maxRetries}) from: ${modelUrl}`);
          
          await Promise.all([
            faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl),
            faceapi.nets.faceLandmark68Net.loadFromUri(modelUrl),
            faceapi.nets.faceRecognitionNet.loadFromUri(modelUrl)
          ]);
          
          setModelsLoaded(true);
          console.log('Face-api models loaded successfully from:', modelUrl);
          lastError = null;
          break;
        } catch (error) {
          console.error(`Error loading face-api models (attempt ${attempt}) from ${modelUrl}:`, error);
          lastError = error;
          
          // On first failure, try CDN on next attempt
          if (attempt === 1) {
            modelUrl = cdnModelUrl;
            console.log('Falling back to CDN models...');
          }
        }
      }
      
      if (lastError) {
        toast.error('Failed to load face recognition models. Please try again later.');
      }
      
      setLoadingModels(false);
    };

    if (open) {
      loadModels();
      fetchEnrollmentStatus();
    }
  }, [open]);

  const fetchEnrollmentStatus = async () => {
    try {
      const response = await axios.get(`${API}/face/enrollment-status`);
      setEnrollmentStatus(response.data);
    } catch (error) {
      console.error('Error fetching enrollment status:', error);
    }
  };

  // Start camera
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      streamRef.current = stream;
      setCameraActive(true);
      
      // Wait for state update to render the video element
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          
          // Handle both cases: video already loaded or loading
          const handleVideoReady = () => {
            if (videoRef.current) {
              videoRef.current.play().catch(err => console.error('Video play error:', err));
              startFaceDetection();
            }
          };
          
          if (videoRef.current.readyState >= 2) {
            // Video is already ready
            handleVideoReady();
          } else {
            // Wait for video to be ready
            videoRef.current.onloadeddata = handleVideoReady;
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      toast.error('Failed to access camera. Please check permissions.');
    }
  };

  // Stop camera
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    setCameraActive(false);
    setFaceDetected(false);
    setDetecting(false);
  }, []);

  // Face detection loop
  const startFaceDetection = () => {
    setDetecting(true);
    
    detectionIntervalRef.current = setInterval(async () => {
      if (videoRef.current && videoRef.current.readyState === 4) {
        const detection = await faceapi
          .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
          .withFaceLandmarks()
          .withFaceDescriptor();
        
        if (detection) {
          setFaceDetected(true);
          
          // Draw detection on canvas
          if (canvasRef.current) {
            const dims = faceapi.matchDimensions(canvasRef.current, videoRef.current, true);
            const resizedDetection = faceapi.resizeResults(detection, dims);
            
            canvasRef.current.getContext('2d').clearRect(0, 0, dims.width, dims.height);
            faceapi.draw.drawDetections(canvasRef.current, resizedDetection);
            faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetection);
          }
        } else {
          setFaceDetected(false);
          if (canvasRef.current) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          }
        }
      }
    }, 200);
  };

  // Capture face
  const captureFace = async () => {
    if (!videoRef.current || !faceDetected) return;
    
    setEnrolling(true);
    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current, new faceapi.TinyFaceDetectorOptions())
        .withFaceLandmarks()
        .withFaceDescriptor();
      
      if (detection) {
        // Capture image for preview
        const canvas = document.createElement('canvas');
        canvas.width = videoRef.current.videoWidth;
        canvas.height = videoRef.current.videoHeight;
        canvas.getContext('2d').drawImage(videoRef.current, 0, 0);
        const imageData = canvas.toDataURL('image/jpeg');
        
        setCapturedImage(imageData);
        setCapturedDescriptor(Array.from(detection.descriptor));
        stopCamera();
        
        toast.success('Face captured successfully!');
      } else {
        toast.error('No face detected. Please try again.');
      }
    } catch (error) {
      console.error('Error capturing face:', error);
      toast.error('Failed to capture face');
    } finally {
      setEnrolling(false);
    }
  };

  // Enroll face
  const enrollFace = async () => {
    if (!capturedDescriptor) return;
    
    setEnrolling(true);
    try {
      await axios.post(`${API}/face/enroll`, {
        face_descriptor: capturedDescriptor
      });
      
      toast.success('Face enrolled successfully!');
      setEnrollmentStatus({ has_face_enrolled: true, enrolled_at: new Date().toISOString() });
      onEnrollmentComplete?.();
      handleClose();
    } catch (error) {
      console.error('Error enrolling face:', error);
      toast.error(error.response?.data?.detail || 'Failed to enroll face');
    } finally {
      setEnrolling(false);
    }
  };

  // Delete enrollment
  const deleteEnrollment = async () => {
    try {
      await axios.delete(`${API}/face/enrollment`);
      toast.success('Face enrollment deleted');
      setEnrollmentStatus({ has_face_enrolled: false, enrolled_at: null });
      setCapturedDescriptor(null);
      setCapturedImage(null);
    } catch (error) {
      console.error('Error deleting enrollment:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete enrollment');
    }
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedDescriptor(null);
    setCapturedImage(null);
    startCamera();
  };

  // Handle close
  const handleClose = () => {
    stopCamera();
    setCapturedDescriptor(null);
    setCapturedImage(null);
    onOpenChange(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2">
            <Camera className="w-5 h-5 text-blue-600" />
            Face Enrollment
          </DialogTitle>
          <DialogDescription>
            {enrollmentStatus?.has_face_enrolled 
              ? "You have already enrolled your face. You can update or delete it below."
              : "Enroll your face for secure clock-in verification."
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Loading models */}
          {loadingModels && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="mt-2 text-sm text-slate-500">Loading face recognition models...</p>
            </div>
          )}

          {/* Enrollment status */}
          {!loadingModels && enrollmentStatus?.has_face_enrolled && !cameraActive && !capturedImage && (
            <Card className="border-green-200 bg-green-50">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="font-medium text-green-800">Face Enrolled</p>
                    <p className="text-sm text-green-600">
                      Enrolled on {formatDate(enrollmentStatus.enrolled_at)}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={startCamera}
                    className="gap-1"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Update Face
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={deleteEnrollment}
                    className="gap-1 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Camera view */}
          {modelsLoaded && !enrollmentStatus?.has_face_enrolled && !cameraActive && !capturedImage && (
            <div className="flex flex-col items-center py-6">
              <UserCircle className="w-24 h-24 text-slate-300" />
              <p className="mt-4 text-slate-500 text-center">
                Click the button below to start the camera and capture your face.
              </p>
              <Button 
                onClick={startCamera} 
                className="mt-4 gap-2 bg-blue-600 hover:bg-blue-700"
                data-testid="start-camera-btn"
              >
                <Camera className="w-4 h-4" />
                Start Camera
              </Button>
            </div>
          )}

          {/* Video preview */}
          {cameraActive && (
            <div className="relative">
              <div className="relative rounded-lg overflow-hidden bg-black" style={{ aspectRatio: '4/3', minHeight: '300px' }}>
                <video 
                  ref={videoRef} 
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  playsInline
                  style={{ transform: 'scaleX(-1)', minHeight: '300px' }}
                />
                <canvas 
                  ref={canvasRef} 
                  className="absolute inset-0 w-full h-full pointer-events-none"
                  style={{ transform: 'scaleX(-1)' }}
                />
                
                {/* Face detection indicator */}
                <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${
                  faceDetected 
                    ? 'bg-green-500 text-white' 
                    : 'bg-yellow-500 text-white'
                }`}>
                  {faceDetected ? (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      Face Detected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Looking for face...
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 flex justify-center gap-3">
                <Button 
                  onClick={stopCamera}
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={captureFace}
                  disabled={!faceDetected || enrolling}
                  className="gap-2 bg-green-600 hover:bg-green-700"
                  data-testid="capture-btn"
                >
                  {enrolling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Camera className="w-4 h-4" />
                  )}
                  Capture Face
                </Button>
              </div>
            </div>
          )}

          {/* Captured preview */}
          {capturedImage && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-black aspect-video">
                <img 
                  src={capturedImage} 
                  alt="Captured face" 
                  className="w-full h-full object-cover"
                  style={{ transform: 'scaleX(-1)' }}
                />
                <Badge className="absolute top-4 left-4 bg-green-500">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Face Captured
                </Badge>
              </div>

              <div className="flex justify-center gap-3">
                <Button 
                  onClick={retakePhoto}
                  variant="outline"
                  className="gap-1"
                >
                  <RefreshCw className="w-4 h-4" />
                  Retake
                </Button>
                <Button 
                  onClick={enrollFace}
                  disabled={enrolling}
                  className="gap-2 bg-blue-600 hover:bg-blue-700"
                  data-testid="enroll-btn"
                >
                  {enrolling ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4" />
                  )}
                  {enrollmentStatus?.has_face_enrolled ? 'Update Face' : 'Enroll Face'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FaceEnrollment;
